import React from 'react';
import { BookOpenIcon, CreditCardIcon, LockKeyholeIcon, ShieldCheckIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { CTAButton } from './Shared';
import { FlowerOfLife, Merkaba, MetatronCube, VesicaLeaf } from './Ornaments';
import { MOCKUP_URL } from '../data/pack';
export function Hero() {
  const reduceMotion = useReducedMotion();
  return <header className="astral-canvas relative isolate flex min-h-[calc(100svh-2rem)] flex-col justify-center overflow-hidden px-4 pb-6 pt-3 sm:px-6 sm:pb-14 sm:pt-8 lg:px-8" data-id="element-88" data-mp="12a0ljc9tg2v5">
      <FlowerOfLife className="pointer-events-none absolute -right-24 top-2 -z-10 h-64 w-64 opacity-[0.3] md:right-2 md:h-[26rem] md:w-[26rem]" color="#A98BFF" data-id="element-89" data-mp="sbb2vha3iedu" />
      <MetatronCube className="pointer-events-none absolute -bottom-28 -left-28 -z-10 h-72 w-72 opacity-[0.28] md:h-[28rem] md:w-[28rem]" color="#E9C46A" data-id="element-90" data-mp="neaz5d0pxnpw" />
      <Merkaba className="absolute left-[6%] top-1/3 hidden h-12 w-12 opacity-60 lg:block" color="#5FDCD8" data-id="element-91" data-mp="380w0g222h1f9" />
      <VesicaLeaf className="absolute right-[6%] top-[42%] hidden h-14 w-14 opacity-55 lg:block" color="#A98BFF" data-id="element-92" data-mp="3j6o8da1vw4lc" />

      <motion.div className="mx-auto w-full max-w-5xl text-center" initial={reduceMotion ? false : {
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-93" data-mp="39b4l1c1dye87">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/55 bg-brand-paper px-3 py-1 shadow-sm" data-id="element-94" data-mp="2qkmune40zuq0">
          <Merkaba className="h-3.5 w-3.5" color="#2F7D80" data-id="element-95" data-mp="2vj7rr4nzdya8" />
          <span className="font-display text-[10px] font-semibold tracking-[0.18em] text-brand-ink" data-id="element-96" data-mp="2hix224yeqpqn">
            LUMEN AKASHA™
          </span>
        </div>

        <p className="mt-3 font-serif text-sm font-semibold tracking-[0.04em] text-brand-teal sm:text-base" data-id="element-97" data-mp="2lml8gkbazfab">
          Atlas de Geometría Sagrada Aplicada al Cuerpo™
        </p>

        <h1 className="mx-auto mt-1.5 max-w-3xl font-display text-[26px] font-bold leading-[1.12] text-brand-ink sm:text-4xl md:text-5xl" data-id="element-98" data-mp="na0ap37izbga">
          De Terapeuta Intuitivo a{' '}
          <span className="text-brand-gold" data-id="element-99" data-mp="degd8cdbc4pf">Clínico Maestro</span>
        </h1>

        <p className="mx-auto mt-3 max-w-xl font-serif text-lg leading-snug text-brand-text sm:text-xl md:text-2xl" data-id="element-100" data-mp="1b15pdf1qbxp0">
          Protocolo completo. Autoridad visual. Resultados que hablan solos.
        </p>

        <div className="mockup-halo relative mx-auto mt-2 w-full max-w-3xl sm:mt-4" data-id="element-101" data-mp="111om149y9vw7">
          <motion.img src={MOCKUP_URL} alt="Mockup del Atlas de Geometría Sagrada Aplicada al Cuerpo con los 3 tomos y los 6 bonos de regalo" initial={reduceMotion ? false : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: reduceMotion ? 0 : 0.1,
          ease: [0.23, 1, 0.32, 1]
        }} className="mockup-fade mx-auto h-auto max-h-[38svh] w-full object-contain sm:max-h-[34rem]" draggable={false} decoding="async" data-id="element-102" data-mp="x1w80c1sexjg" />
        </div>

        <div className="mt-4 flex flex-col items-center gap-3 sm:mt-6 sm:gap-4" data-id="element-103" data-mp="2kyduattlxwh3">
          <CTAButton className="w-full max-w-[23rem] px-5 py-3.5 text-[13px] leading-tight sm:w-auto sm:max-w-none sm:px-10 sm:py-4 sm:text-base md:text-lg" data-id="element-104" data-mp="2p0h7bmvxd94h">
            → QUIERO MI ATLAS DE GEOMETRÍA AHORA
          </CTAButton>

          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-medium text-brand-tealLight sm:text-xs" data-id="element-105" data-mp="un9bbrw7qrh9">
            <span data-id="element-106" data-mp="znn6488t04xc">3 tomos + 6 bonos</span>
            <span aria-hidden="true" className="text-brand-gold" data-id="element-107" data-mp="4aupsu6335pd">
              ·
            </span>
            <span data-id="element-108" data-mp="3vi6yekswiato">150 páginas</span>
            <span aria-hidden="true" className="text-brand-gold" data-id="element-109" data-mp="3vh5leasbre9b">
              ·
            </span>
            <span data-id="element-110" data-mp="e91uoen3lxb0">100% PDF</span>
            <span aria-hidden="true" className="text-brand-gold" data-id="element-111" data-mp="e77grka8v5gf">
              ·
            </span>
            <span data-id="element-112" data-mp="3hqa8mnepgd0g">Acceso en 5 min</span>
          </div>

          <div className="hidden flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-brand-muted sm:flex md:text-sm" data-id="element-113" data-mp="3hr22ntsnm2om">
            <span className="inline-flex items-center gap-1.5" data-id="element-114" data-mp="3cp30uockbdts">
              <ShieldCheckIcon className="h-4 w-4 text-brand-teal" data-id="element-115" data-mp="3rh566nxeiz17" />
              Garantía 7 días
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-116" data-mp="3mnsm8r3o3ges">
              <BookOpenIcon className="h-4 w-4 text-brand-teal" data-id="element-117" data-mp="2yyzkuf4a0gcs" />
              Usalo en tablet durante la sesión
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-118" data-mp="2u06hvgmm87qv">
              <LockKeyholeIcon className="h-4 w-4 text-brand-teal" data-id="element-119" data-mp="19i5igtvknsmf" />
              Pago seguro
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-120" data-mp="1eafbwjvwbatg">
              <CreditCardIcon className="h-4 w-4 text-brand-teal" data-id="element-121" data-mp="zkhgpjzjtkur" />
              Mercado Pago
            </span>
          </div>
        </div>
      </motion.div>
    </header>;
}